import moment from 'moment';
import templateUrl from './adjust-division-rotations-modal.component.html';

export const AdjustDivisionRotationsModalComponent = {
    controller: AdjustDivisionRotationsModalController,
    controllerAs: "vm",
    bindings: {
        resolve: "<",
        close: "&",
        dismiss: "&"
    },
    templateUrl
};

export const AdjustDivisionRotationsModal = {
    component: "apexAdjustDivisionRotationsModal",
    windowClass: 'xx-dialog pagespinner',
    size: 'lg',
    backdrop: 'static'
};

function AdjustDivisionRotationsModalController(rotationsService, utilityService) {
    'ngInject';
    const vm = this;

    vm.$onInit = onInit;
    vm.adjustment;
    vm.editForm;
    vm.newRotations = [];
    vm.onChangeRotation = onChangeRotation;
    vm.onSelectAdjustedRotation = onSelectAdjustedRotation;
    vm.onAdjustNext = onAdjustNext;
    vm.onAdjustPrevious = onAdjustPrevious;
    vm.onInsertNext = onInsertNext;
    vm.onInsertPrevious = onInsertPrevious;
    vm.onDeleteNext = onDeleteNext;
    vm.onDeletePrevious = onDeletePrevious;
    vm.onSubmit = onSubmit;
    vm.warnAboutLockedPayrolls;

    // $uibModal injected values.
    vm.resolve;
    vm.close;
    vm.dismiss;

    function onInit() {
        vm.divisionProfile = vm.resolve.divisionProfile;
        vm.adjustment = new DivisionRotationAdjustment(vm.divisionProfile.DivisionProfileID);
        vm.warnAboutLockedPayrolls = true;

        const loadInitialData = rotationsService.retrieveDivisionRotations({
            divisionProfileID: vm.divisionProfile.DivisionProfileID
        }).then(function (rotations) {
            vm.rotations = rotations.map(function (rotation) {
                rotation.Text = kendo.format("{0:MM/dd/yyyy}-{1:MM/dd/yyyy}", rotation.StartDate, rotation.EndDate);
                return rotation;
            }).sort(function (a, b) {
                return a.StartDate < b.StartDate ? -1 : 1;
            });

            vm.originalRotations = vm.rotations.map(function (rotation) {
                return {
                    startDate: rotation.StartDate,
                    endDate: rotation.EndDate
                };
            })
        });

        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(loadInitialData, "Initial load failed"));

        // TODO: When making a change to a rotation, load all payrolls for the deleted rotation
        // for this division and all payrolls for the new rotation for companies that are in
        // the division.
    }

    function onSelectAdjustedRotation(rotation) {
        // Start over. Remove any adjustments aside from the created adjustment.
        if (rotation) {
            const adjustedRotation = {
                currentRotation: {
                    startDate: rotation.StartDate,
                    endDate: rotation.EndDate
                },
                newRotation: {
                    startDate: rotation.StartDate,
                    endDate: rotation.EndDate
                }
            };
            vm.adjustment.adjustedRotations = [adjustedRotation];
            vm.newRotations = [adjustedRotation.newRotation];
        } else {
            vm.newRotations = [];
        }
        vm.adjustment.newRotations = [];
        vm.adjustment.deletedRotations = [];
        if (rotation) {
            onChangeRotation(rotation);
        }

        if (vm.editForm) {
            // No edits have been made by default.
            vm.editForm.$setPristine(true);
        }
    }

    function onChangeRotation(rotation) {
        // Certain logic changes if the edit form was invalid the last time this was called.
        const previouslyValid = vm.editForm && vm.editForm.$valid;
        const isValid = validateAdjustment();

        if (previouslyValid && isValid) {
            clearNoOps();
        }

        if (vm.editForm) {
            // No edits have been made by default.
            vm.editForm.$setDirty(true);
        }
    }

    function clearNoOps() {
        // Remove no-ops from the adjustment.
        vm.adjustment.clearNoOpAdjustments(vm.originalRotations);
        vm.newRotations = vm.adjustment.adjustedRotations.map(function (adjustedRotation) {
            return adjustedRotation.newRotation;
        }).concat(vm.adjustment.newRotations)
            .sort(sortRotation);
    }

    function validateAdjustment() {
        const resultRotations = vm.adjustment.transformResultingRotations(vm.originalRotations);
        // Split the adjustment into deleted and updated rotations for comparison.
        const newRotations = vm.adjustment.adjustedRotations.map(function (p) {
            return p.newRotation;
        }).concat(vm.adjustment.newRotations).sort(sortRotation);
        const deletedRotations = vm.adjustment.adjustedRotations.map(function (p) {
            return p.currentRotation;
        }).concat(vm.adjustment.deletedRotations).sort(sortRotation);

        vm.newRotations.sort(sortRotation); // Sort the rotation list.

        // Perform validations:
        // 1. There cannot be any rotations in between adjusted rotations that are not adjusted.
        // Nothing wrong with this conceptually, but it would make the UI harder to understand.
        // Also, we want to discourage performing complex adjustments all in one update.
        vm.rotationsBetweenAdjustedRotations = validateUntouchedRotationsBetweenNewRotations(
            resultRotations, newRotations);

        // 2. There cannot be any rotations in conflict. Maintain a separate list of rotations
        // in conflict before and after the chunk of adjustments.
        //     a. Keep a separate list of rotations in conflict that are actually fully emcompassed by another rotation.
        //     b. Keep a list of dates from deleted rotations that are not contained in the new rotations.
        const conflictingRotations = validateRotationConflicts(resultRotations)
            // Group the conflicts based on whether they are before, after, or within the adjustment.
            .reduce(function (conflictGroups, conflict) {
                if (rotationsEqual(conflict.left, conflict.right)) {
                    // Ignore the conflict. It can be resolved simply by discarding the adjustment.
                    return conflictGroups;
                }

                // Only look at the first and the last new rotations.
                let originalRotation;
                const isLeftAdjusted = newRotations.filter(function (rotation) {
                    return rotationsEqual(rotation, conflict.left)
                }).length > 0;
                const isRightAdjusted = newRotations.filter(function (rotation) {
                    return rotationsEqual(rotation, conflict.right);
                }).length > 0;
                if (isLeftAdjusted && !isRightAdjusted) {
                    // Left is a boundary to the adjustment
                    originalRotation = conflict.right;
                } else if (!isLeftAdjusted && isRightAdjusted) {
                    originalRotation = conflict.left;
                }
                const isEncompassed = originalRotation
                    && originalRotation.startDate >= newRotations[0].startDate
                    && originalRotation.endDate <= newRotations[newRotations.length - 1].endDate;

                if (!originalRotation) {
                    // Both conflicts come from rotations that are being edited within the adjustment.
                    // ie, the adjustment as a whole is in conflict and needs to be resolved.
                    conflictGroups.within.push(conflict);
                } else if (originalRotation === conflict.left) {
                    // The conflict is considered a previous conflict if the original rotation is to the left.
                    if (isEncompassed) {
                        conflictGroups.before.encompassed.push(conflict);
                    } else {
                        conflictGroups.before.normal.push(conflict);
                    }
                } else {
                    // Treat any other conflicts as being after the adjustment.
                    if (isEncompassed) {
                        conflictGroups.after.encompassed.push(conflict);
                    } else {
                        conflictGroups.after.normal.push(conflict);
                    }
                }

                return conflictGroups;
            }, { before: { normal: [], encompassed: [] }, after: { normal: [], encompassed: [] }, within: [] });
        vm.previousConflicts = conflictingRotations.before;
        vm.afterConflicts = conflictingRotations.after;
        vm.adjustmentConflicts = conflictingRotations.within;

        // 3. Dates cannot be removed from a rotation.
        const removedDates = Array.from(deletedRotations.reduce(function (removedDates, deleted) {
            for (let date = deleted.startDate; moment(date).isSameOrBefore(deleted.endDate); date = moment(date).add(1, 'day').toDate()) {
                const onNewRotation = newRotations.filter(filterDateBetween(date)).length > 0;
                if (!onNewRotation) {
                    removedDates.add(date.toISOString());
                }
            }
            return removedDates;
        }, new Set())).map(function (isoString) {
            return new Date(isoString);
        }).sort(function (a, b) {
            return a < b ? -1 : 1;
        });
        const earliestNewDate = newRotations[0].startDate;
        const latestNewDate = newRotations[newRotations.length - 1].endDate;
        vm.missingPreviousRotation = removedDates.filter(function (date) {
            return moment(date).isBefore(earliestNewDate);
        }).reduce(reduceRotationFromSortedDateList, null);
        vm.missingNextRotation = removedDates.filter(function (date) {
            return moment(date).isAfter(latestNewDate);
        }).reduce(reduceRotationFromSortedDateList, null);
        vm.missingDates = removedDates;

        // Also make sure none of the adjustments are impossible.
        const allRotationsValid = newRotations.reduce(function (all, rotation) {
            return all && rotation.startDate <= rotation.endDate;
        }, true);

        // Note: vm.rotationsBetweenAdjustedRotations are not actually invalid.
        // They are just difficult to represent on the edit form.
        const isValid = !vm.previousConflicts.normal.length
            && !vm.previousConflicts.encompassed.length
            && !vm.afterConflicts.normal.length
            && !vm.afterConflicts.encompassed.length
            && !vm.adjustmentConflicts.length
            && !vm.missingNextRotation
            && !vm.missingPreviousRotation
            && !vm.missingDates.length
            && allRotationsValid;

        if (vm.editForm) {
            const form = vm.editForm;
            form.$setValidity("conflict", !vm.previousConflicts.normal.length
                && !vm.previousConflicts.encompassed.length
                && !vm.afterConflicts.normal.length
                && !vm.afterConflicts.encompassed.length);
            // Sparse, meaning that there are gaps in a rotation's coverage.
            form.$setValidity("sparse", !vm.missingNextRotation && !vm.missingPreviousRotation && !vm.missingDates.length);
            form.$setValidity("date-ranges", allRotationsValid);
        }

        return isValid;

    }

    function getPreviousRotation() {
        const rotations = vm.adjustment.transformResultingRotations(vm.originalRotations); // already sorted.
        const firstAdjustment = vm.newRotations[0];
        const previousRotations = rotations.filter(function (rotation) {
            return rotation.endDate < firstAdjustment.endDate && rotation.startDate <= firstAdjustment.startDate;
        });
        // Last item in the list.
        return previousRotations[previousRotations.length - 1] || {};
    }

    function getNextRotation() {
        const lastAdjustment = vm.newRotations[vm.newRotations.length - 1];
        const rotations = vm.adjustment.transformResultingRotations(vm.originalRotations);

        // Find the first rotation after the list of current adjustments and adjust it as well.
        const nextRotation = rotations.filter(function (rotation) {
            return rotation.endDate > lastAdjustment.startDate
                && vm.newRotations.filter(function (newRotation) {
                    return rotationsEqual(rotation, newRotation);
                }).length === 0;
        })[0];

        return nextRotation || {};
    }

    /**
     * Return a filter function that filters rotations to only include rotations where
     * the given date is between the rotation start and end date.
     * @param {any} date
     */
    function filterDateBetween(date) {
        return function filterRotationDates(rotation) {
            return date >= rotation.startDate && date <= rotation.endDate
        };
    }

    function addRotationAdjustment(rotation) {
        const newRotation = {
            startDate: rotation.startDate,
            endDate: rotation.endDate
        };
        vm.newRotations.push(newRotation);
        vm.newRotations.sort(sortRotation);
        vm.adjustment.adjustedRotations.push({
            currentRotation: rotation,
            newRotation: newRotation
        });
        return newRotation;
    }

    function addNewRotation(rotation) {
        const newRotation = {
            startDate: rotation.startDate,
            endDate: rotation.endDate
        };
        // Update the list of new rotations used to display the ordered list in the view.
        vm.newRotations.push(newRotation);
        vm.newRotations.sort(sortRotation);
        vm.adjustment.newRotations.push(newRotation);
    }

    function addDeletedRotation(rotation) {
        vm.adjustment.deletedRotations.push(rotation);
    }

    function onAdjustNext() {
        const nextRotation = getNextRotation();
        if (nextRotation) {
            const newRotation = addRotationAdjustment(nextRotation);
            if (vm.missingNextRotation) {
                newRotation.startDate = vm.missingNextRotation.startDate;
            }
            onChangeRotation(newRotation);
        }
    }

    function onAdjustPrevious() {
        // Find the latest rotation before the list of current adjustments and adjust it as well.
        const previousRotation = getPreviousRotation();
        if (previousRotation) {
            const newRotation = addRotationAdjustment(previousRotation);
            if (vm.missingPreviousRotation) {
                newRotation.endDate = vm.missingPreviousRotation.endDate;
            }
            onChangeRotation(newRotation);
        }
    }

    function onInsertNext() {
        // If there is a missing next rotation, insert that. Otherwise, get the next rotation
        // following the range of the adjusted rotations.
        const nextRotation = vm.missingNextRotation || getNextRotation();
        if (nextRotation) {
            addNewRotation(nextRotation);
        }
        onChangeRotation(nextRotation);
    }

    function onInsertPrevious() {
        // If there is a missing previous rotation, insert that. Otherwise, get the previous rotation
        // preceding the range of the adjusted rotations.
        const previousRotation = vm.missingPreviousRotation || getPreviousRotation();
        if (previousRotation) {
            addNewRotation(previousRotation);
        }
        onChangeRotation(previousRotation);
    }

    function onDeleteNext() {
        const nextRotation = getNextRotation();
        if (nextRotation) {
            addDeletedRotation(nextRotation);
        }
        onChangeRotation(nextRotation);
    }

    function onDeletePrevious() {
        const previousRotation = getPreviousRotation();
        if (previousRotation) {
            addDeletedRotation(previousRotation);
        }
        onChangeRotation(previousRotation);
    }

    function onSubmit() {
        const isValid = validateAdjustment();

        vm.lockedPayrolls = null;
        const adjustments = {
            AdjustedRotations: vm.adjustment.adjustedRotations.map(function (adjustedRotation) {
                return {
                    CurrentRotation: mapRotation(adjustedRotation.currentRotation),
                    NewRotation: mapRotation(adjustedRotation.newRotation)
                }
            }),
            NewRotations: vm.adjustment.newRotations.map(mapRotation),
            DeletedRotations: vm.adjustment.deletedRotations.map(mapRotation),
            IgnoreLockedPayrolls: !vm.warnAboutLockedPayrolls
        };
        if (isValid) {
            const request = rotationsService.adjustDivisionRotations(vm.adjustment.divisionProfileID, adjustments)
                .then(function () {
                    alert("The rotations were adjusted successfully");
                    // Apply the transformations and resolve the results in the modal.
                    vm.originalRotations = vm.adjustment.transformResultingRotations(vm.originalRotations);
                    // Map back to the format produced from the API.
                    vm.rotations = vm.originalRotations.map(function (rotation) {
                        return {
                            StartDate: rotation.startDate,
                            EndDate: rotation.endDate
                        };
                    });
                    vm.close({ $value: { rotations: vm.rotations } });
                })
                .catch(function (ex) {
                    if (!ex.data || !ex.data.ModelState || !ex.data.ModelState["request.IgnoreLockedPayrolls"]) {
                        // Propagate the exception.
                        throw ex;
                    }
                    if (ex && ex.data && ex.data.ModelState) {
                        vm.modelState = ex.data.ModelState;
                    }
                    // Otherwise, display the list of locked payrolls that are blocking the adjustment.
                    // This is only a validation error when IgnoreLockedPayrolls is false.
                    vm.lockedPayrolls = ex.data.ModelState["request.IgnoreLockedPayrolls"];
                });

            utilityService.showLoadingUntilResolved(
                utilityService.alertOnError(request, "Saving adjustments failed"));

        }
        /**
         * Map the given rotation from lower camel case to an upper camel case model.
         * @param {any} rotation
         */
        function mapRotation(rotation) {
            return {
                StartDate: rotation.startDate,
                EndDate: rotation.endDate
            }
        }
    }

    /**
     * Reduce the list of dates a into a rotation starting at the earliest date
     * and ending on the latest date.
     * @param {any} rotation
     * @param {any} date
     * @param {any} i
     * @param {any} a
     */
    function reduceRotationFromSortedDateList(rotation, date, i, a) {
        // No need to look at each element, just create the aggregate directly from the array.
        if (rotation) {
            return rotation;
        }
        return {
            startDate: a[0],
            endDate: a[a.length - 1]
        };
    }

    /**
     * Return any untouched rotations that are in the middle of the adjustment.
     * @param {any} rotations
     * @param {any} newRotations
     */
    function validateUntouchedRotationsBetweenNewRotations(rotations, newRotations) {
        return rotations.filter(function (rotation) {
            // Only look at rotations not in the new rotations list.
            const isAdjusted = newRotations.filter(function (newRotation) {
                return rotationsEqual(rotation, newRotation)
            }).length > 0;
            if (isAdjusted) {
                return false;
            }
            const isBetweenAdjustedRotations = newRotations.reduce(function (ag, newRotation) {
                const compare = newRotation.startDate < rotation.startDate ? -1 : 1;
                if (compare < 0) {
                    ag.anyBefore = true;
                } else if (compare > 0) {
                    ag.anyAfter = true;
                }
                return ag;
            }, { anyAfter: false, anyBefore: false });
            return isBetweenAdjustedRotations.anyBefore && isBetweenAdjustedRotations.anyAfter;
        });
    }

    /**
     * Return the aggregate list of all rotations in the given list that are in conflict with their immediate neighbours,
     * where the left rotation in conflict is always before the right rotation.
     * @param {any} rotations
     */
    function validateRotationConflicts(rotations) {
        return rotations.reduce(function (ag, rotation) {
            if (ag.prev) {
                if (moment(rotation.startDate).isSameOrBefore(ag.prev.endDate)
                    && moment(ag.prev.startDate).isSameOrBefore(rotation.endDate)) {
                    ag.conflicts.push({
                        left: ag.prev,
                        right: rotation
                    });
                }
            }
            ag.prev = rotation;
            return ag;
        }, { prev: null, conflicts: [] }).conflicts;
    }

    /**
     * A class that represents a rotation adjustment in progress.
     * @param {any} divisionProfileID
     */
    class DivisionRotationAdjustment {
        constructor(divisionProfileID) {
            this.divisionProfileID = divisionProfileID;
            this.adjustedRotations = [];
            this.newRotations = [];
            this.deletedRotations = [];
        }
    }

    /**
     * Calculate the result of running the rotation adjustment on the given list of rotations.
     */
    DivisionRotationAdjustment.prototype.transformResultingRotations = function (rotations) {
        const self = this;
        return rotations.filter(function (rotation) {
            // Filter out rotations that were completely deleted.
            return self.deletedRotations.filter(function (deleted) {
                return rotationsEqual(deleted, rotation);
            }).length === 0;
        }).map(function (rotation) {
            const adjustedRotation = self.adjustedRotations.filter(function (adjustment) {
                const currentRotation = adjustment.currentRotation;
                return rotationsEqual(currentRotation, rotation);
            })[0];
            // Replace the rotation with the adjusted new rotation if an adjustment was found.
            return adjustedRotation ? adjustedRotation.newRotation : rotation;
        }).concat(self.newRotations) // Add all new rotations to the rotation list.
            .sort(sortRotation)
    }

    DivisionRotationAdjustment.prototype.clearNoOpAdjustments = function (rotations) {
        // Remove adjustments that will have no effect of the given list of rotations.
        const self = this;
        self.newRotations = self.newRotations.reduce(function (newRotations, newRotation) {
            // Rebuild the new rotations list, excluding unnecessary actions.
            const isFound = rotations.filter(function (rotation) {
                return rotationsEqual(newRotation, rotation);
            }).length > 0;
            if (!isFound) {
                newRotations.push(newRotation);
            }
            return newRotations;
        }, []);
        self.adjustedRotations = self.adjustedRotations.reduce(function (adjustedRotations, adjustedRotation, i) {
            const isFound = rotations.filter(function (rotation) {
                return rotationsEqual(adjustedRotation.newRotation, rotation);
            }).length > 0;
            if (!isFound || i === 0) {
                // Never remove the first adjustment in the list.
                adjustedRotations.push(adjustedRotation);
            }
            return adjustedRotations;
        }, []);
        self.deletedRotations = self.deletedRotations.reduce(function (deletedRotations, deletedRotation) {
            const isFound = rotations.filter(function (rotation) {
                return rotationsEqual(deletedRotation, rotation);
            }).length > 0;
            if (isFound) {
                // Remove the deletion if the rotation is already not found in the deletions list.
                deletedRotations.push(deletedRotation);
            }
            return deletedRotations;
        }, []);
    }

    function sortRotation(a, b) {
        // Sort by start date. A valid rotation list will never have duplicate start dates.
        if (a.startDate < b.startDate) {
            return -1;
        } else if (a.startDate > b.startDate) {
            return 1;
        }
        if (a.endDate < b.endDate) {
            return -1;
        } else if (a.endDate > b.endDate) {
            return 1;
        }
        return 0;
    }


    /**
     * Return whether the two rotations a and b are equal.
     * @param {any} a
     * @param {any} b
     */
    function rotationsEqual(a, b) {
        return moment(a.startDate).isSame(b.startDate)
            && moment(a.endDate).isSame(b.endDate);
    }
}
